import { teamColors } from '../../theme/tokens.js';
import Icon from '../common/Icon.jsx';

const POSITION_ACCENTS = {
  1: { ring: 'ring-yellow-500/60', bg: 'bg-yellow-500/15', text: 'text-yellow-400' },
  2: { ring: 'ring-zinc-400/60', bg: 'bg-zinc-400/15', text: 'text-zinc-200' },
  3: { ring: 'ring-amber-700/60', bg: 'bg-amber-700/15', text: 'text-amber-500' },
};

export default function DriverRow({ result, expanded, onToggle }) {
  const livery = teamColors[result.team_name] ?? '#666';
  const positionStyle = POSITION_ACCENTS[result.position] ?? null;

  const isDNS = result.status === 'DNS';

  const hasGrid = typeof result.grid === 'number';
  const gridDelta = hasGrid ? result.grid - result.position : 0;
  const gridDeltaLabel =
    !hasGrid ? '' : gridDelta === 0 ? '— ' : gridDelta > 0 ? `▲${gridDelta}` : `▼${Math.abs(gridDelta)}`;
  const gridDeltaColor =
    gridDelta > 0 ? 'text-signal-green' : gridDelta < 0 ? 'text-f1-red' : 'text-ink-tertiary';

  return (
    <div className={`relative overflow-hidden rounded-xl bg-bg-card ring-1 ring-stroke transition-colors hover:bg-bg-card2 ${isDNS ? 'opacity-50' : ''}`}>
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 h-full w-[3px]"
        style={{ backgroundColor: livery }}
      />

      <button
        onClick={onToggle}
        className="pressable flex w-full items-center gap-3 px-3 py-3 text-left"
        aria-expanded={expanded}
      >
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-mono text-sm font-bold tabular-nums ${
            positionStyle
              ? `${positionStyle.bg} ${positionStyle.text} ring-1 ${positionStyle.ring}`
              : 'bg-bg-card2 text-ink-secondary ring-1 ring-stroke'
          }`}
        >
          {result.position}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-ink-primary">
              {result.driver_name}
            </span>
            {result.hasFastestLap ? (
              <span className="inline-flex items-center gap-0.5 rounded-md bg-signal-purple/15 px-1.5 py-0.5 font-mono text-2xs font-bold tracking-wider text-signal-purple">
                <Icon name="star" size={10} />
                FL
              </span>
            ) : null}
            <span className="font-mono text-2xs text-ink-tertiary">#{result.driver_number}</span>
          </div>
          <div className="truncate text-2xs uppercase tracking-widest-plus text-ink-tertiary">
            {result.team_name}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end">
          <span className="font-mono text-xs font-semibold tabular-nums text-ink-primary">
            {result.time_gap}
          </span>
          <span className="mt-0.5 flex items-center gap-1 text-2xs uppercase tracking-wider text-ink-tertiary">
            {result.status !== 'Finished' && result.status !== 'Lapped' ? (
              <ResultStatusBadge status={result.status} />
            ) : result.points > 0 ? (
              <>
                <span>+{result.points} pts</span>
                {result.hasFastestLap && result.flBonusActive ? (
                  <span className="rounded-sm bg-signal-purple/15 px-1 font-mono text-[10px] font-bold text-signal-purple">+1</span>
                ) : null}
              </>
            ) : null}
            {result.hasFastestLap && result.status === 'Finished' && result.points === 0 && result.flBonusActive ? (
              <span className="rounded-sm bg-signal-purple/15 px-1 font-mono text-[10px] font-bold text-signal-purple">+1</span>
            ) : null}
          </span>
        </div>

        <span
          className={`ml-1 shrink-0 text-ink-tertiary transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
        >
          <Icon name="chevronDown" size={16} />
        </span>
      </button>

      {expanded ? (
        <div className="animate-slide-down border-t border-stroke px-3 pb-3 pt-2">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Grid → Finish" value={hasGrid ? `${result.grid} → ${result.position}` : `P${result.position}`} accent={gridDeltaColor} delta={gridDeltaLabel} />
            <Stat label="Laps" value={result.laps} />
            <Stat label="Status" value={result.status} accent={statusColor(result.status)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, accent = 'text-ink-primary', delta }) {
  return (
    <div className="rounded-lg bg-bg-card2 px-2 py-2">
      <div className="text-[10px] uppercase tracking-widest-plus text-ink-tertiary">{label}</div>
      <div className={`mt-1 font-mono text-xs font-semibold tabular-nums ${accent}`}>
        {value}
        {delta ? <span className="ml-1 text-[10px]">{delta}</span> : null}
      </div>
    </div>
  );
}

function ResultStatusBadge({ status }) {
  const color = statusColor(status);
  return <span className={`inline-flex rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-bold ${color} ${statusBg(status)}`}>{status}</span>;
}

function statusBg(status) {
  switch (status) {
    case 'DNF': return 'bg-f1-red/10';
    case 'DNS': return 'bg-ink-muted/10';
    case 'DSQ': return 'bg-signal-purple/10';
    default: return '';
  }
}

function statusColor(status) {
  switch (status) {
    case 'Finished': return 'text-signal-green';
    case 'Lapped': return 'text-signal-yellow';
    case 'DNF': return 'text-f1-red';
    case 'DNS': return 'text-ink-muted';
    case 'DSQ': return 'text-signal-purple';
    default: return 'text-ink-primary';
  }
}
